import { ref, computed, type Ref } from 'vue'
import { defineStore } from 'pinia'
import { OnboardingStatus, ResponseError } from '@roadfootage/api-client'
import type {
  UserProfileDetailsDto,
  UserProfileDto,
  UserPreferencesDto,
  AccountDeletionDto,
  Operation
} from '@roadfootage/api-client'
import type { Operation as JsonPatchOperation } from 'fast-json-patch'

import logger from '../../logger'
import { useApi } from '../../composables/api'
import { useWalletStore } from '../wallet'

export const useUserStore = defineStore('user', () => {
  const userProfile: Ref<UserProfileDto | null> = ref(null)
  const userPreferences: Ref<UserPreferencesDto | null> = ref(null)

  const accountDeletion: Ref<AccountDeletionDto | null> = ref(null)

  const { meApi } = useApi()

  const isProfileSetUp = computed(() => userProfile.value !== null)
  const isPreferencesSetUp = computed(() => userPreferences.value !== null)

  async function loadProfile(): Promise<boolean> {
    userProfile.value = null

    try {
      const response = await meApi.meFetchMe()
      console.log('Loaded profile', response)
      userProfile.value = response.userProfile ?? null
      userPreferences.value = response.userPreferences ?? null
      accountDeletion.value = response.accountDeletion ?? null
      useWalletStore().setWallet(response.userWallet)
      if (response.status !== OnboardingStatus.Complete) return false
    } catch (e) {
      logger.error('Failed to load profile', e)
      throw e
    }
    return true
  }

  async function createProfile(
    userProfileDetailsDto: UserProfileDetailsDto = {},
    turnstileToken: string
  ) {
    userProfile.value = await meApi.meCreateUserProfile({
      userProfileDetailsDto,
      turnstileToken
    })
  }

  async function updateProfile(patchOperations: JsonPatchOperation[]) {
    userProfile.value = await meApi.meUpdateUserProfileDetails({
      operation: patchOperations as Operation[]
    })
  }

  async function savePreferences(userPreferencesDto: UserPreferencesDto = {}) {
    userPreferences.value = await meApi.meSetUserPreferences({
      userPreferencesDto
    })
  }

  async function requestAccountDeletion() {
    accountDeletion.value = await meApi.meRequestAccountDeletion()
  }

  async function cancelAccountDeletion() {
    try {
      await meApi.meCancelAccountDeletion()
    } catch (e) {
      // 404: already cancelled elsewhere, so there is nothing pending any more.
      if (!(e instanceof ResponseError && e.response.status === 404)) throw e
    }
    accountDeletion.value = null
  }

  return {
    userProfile,
    accountDeletion,
    userPreferences,
    isProfileSetUp,
    isPreferencesSetUp,
    loadProfile,
    createProfile,
    updateProfile,
    savePreferences,
    requestAccountDeletion,
    cancelAccountDeletion
  }
})
